/**
 * Cross-resolution tip consensus — the probe's measured algorithm
 * (tip-consensus-probe.test.ts, ratified into the panel design
 * HAND_STUDIO_KNUCKLES.md 2026-07-27), promoted to product. ONE
 * responsibility: sweep a hand across voxel resolutions, pool every branch TIP
 * in world space, cluster by proximity, and rank clusters by
 * distinct-resolution votes — so the panel can offer the UNION of everything
 * any resolution saw, instead of one slider position's view.
 *
 * Why tips: a real digit's tip is stable across resolutions (frog wrist→tip
 * 0.106–0.112 over res 40–72 — "a bad ruler, a good compass") while phantom
 * branches are single-resolution events. Measured: the frog control produced
 * exactly 4 distal clusters per hand at 4–8/8 votes; the monster's
 * un-selectable short fingers ARE in the union at ≥5 votes. The detector is
 * resolution votes × distal reach; a one-resolution cluster is a SUSPECT,
 * shown as one, never hidden.
 *
 * Identity: cluster ids (`tip-N`) are minted in RANK order and are stable for
 * a given subject+hand (deterministic sweep, deterministic clustering — votes
 * are seeded strongest-first with full tie-breaks). This is what lets a digit
 * assignment SURVIVE resolution changes: the thing a person named is a place
 * on the model, not a `branch-N` that renumbers every re-measure (the Codex
 * #77 hazard, solved by construction instead of by clearing).
 *
 * The scan measures; it never decides. No cluster is pre-picked, none is
 * hidden — reach and votes are evidence beside each row (§8).
 *
 * Depends on: hands/hand-evidence (measureHand), hands/hand-candidates (type).
 * Depended on by: shell/hands-panel (via main's deps).
 */
import type { Object3D } from 'three';
import type { HandCandidate } from './hand-candidates.js';
import { measureHand, type Vec3 } from './hand-evidence.js';

/** The default sweep — the probe's measured band (24–80, 8 stops). */
export const CONSENSUS_SWEEP: readonly number[] = [24, 32, 40, 48, 56, 64, 72, 80];

/** Cluster radius as a fraction of the hand span — the probe's measured knob. */
export const CLUSTER_RADIUS_FRACTION = 0.09;

/** One branch tip at one resolution — a vote for "there is a digit here". */
export interface TipVote {
  readonly res: number;
  readonly branchId: string;
  readonly tip: Vec3;
  readonly persistenceVoxels: number;
  /** Straight-line distance wrist→tip, world units. */
  readonly reach: number;
}

export interface TipCluster {
  /** `tip-N`, minted in rank order — the id an assignment is keyed by. */
  readonly id: string;
  /** World-space mean of the member tips. */
  readonly tip: Vec3;
  /** Distinct resolutions that voted, ascending. */
  readonly resolutions: readonly number[];
  /** `resolutions.length` — the rank's first key. */
  readonly votes: number;
  /** Mean member reach as a fraction of the hand span (distal ≈ 1). */
  readonly reachFraction: number;
  /** One resolution only — offered, flagged, never hidden. */
  readonly suspect: boolean;
  /** Strongest single vote (persistence), for row evidence. */
  readonly best: TipVote;
  /** Per-resolution branch id this cluster resolves to (highest-persistence member per res). */
  readonly branchAt: ReadonlyMap<number, string>;
}

export interface TipConsensus {
  readonly handBone: string;
  readonly clusters: readonly TipCluster[];
  /** Resolutions that produced an analyzable hand (the vote denominator). */
  readonly cleanResolutions: readonly number[];
  /** Resolutions that refused, with the refusal — absence is data (§8). */
  readonly refused: readonly { readonly res: number; readonly reason: string }[];
  /** Max member reach, world units — the span reaches are fractions of. */
  readonly span: number;
}

const dist = (a: Vec3, b: Vec3): number => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

interface MutableCluster {
  votes: TipVote[];
  mean: Vec3;
}

/**
 * Greedy proximity clustering, strongest evidence first so weak votes JOIN
 * rather than found. Deterministic: full tie-break on (persistence desc, res
 * asc, branchId asc); means recomputed per join.
 */
export function clusterVotes(votes: readonly TipVote[], radius: number): readonly { votes: readonly TipVote[]; mean: Vec3 }[] {
  const clusters: MutableCluster[] = [];
  const ordered = [...votes].sort(
    (a, b) =>
      b.persistenceVoxels - a.persistenceVoxels || a.res - b.res || a.branchId.localeCompare(b.branchId),
  );
  for (const vote of ordered) {
    let best: MutableCluster | null = null;
    let bestD = radius;
    for (const c of clusters) {
      const d = dist(c.mean, vote.tip);
      if (d < bestD) {
        bestD = d;
        best = c;
      }
    }
    if (best === null) {
      clusters.push({ votes: [vote], mean: vote.tip });
    } else {
      best.votes.push(vote);
      const n = best.votes.length;
      best.mean = {
        x: best.votes.reduce((s, v) => s + v.tip.x, 0) / n,
        y: best.votes.reduce((s, v) => s + v.tip.y, 0) / n,
        z: best.votes.reduce((s, v) => s + v.tip.z, 0) / n,
      };
    }
  }
  return clusters;
}

/**
 * Rank raw clusters and mint `tip-N` ids by the RATIFIED detector — resolution
 * votes × distal reach — then summed persistence, then mean position. The
 * reach factor is load-bearing: wrist artifacts RECUR across resolutions too
 * (frog: 4–8 votes at 9–15% of span), so votes alone would rank a cuff rim
 * above a weak real digit; scaled by reach the digits own the top rows.
 */
export function rankClusters(
  raw: readonly { votes: readonly TipVote[]; mean: Vec3 }[],
  span: number,
): readonly TipCluster[] {
  const byPos = (a: Vec3, b: Vec3): number => a.x - b.x || a.y - b.y || a.z - b.z;
  const stats = raw.map((c) => {
    const resolutions = [...new Set(c.votes.map((v) => v.res))].sort((x, y) => x - y);
    const reach = c.votes.reduce((s, v) => s + v.reach, 0) / c.votes.length;
    return {
      c,
      resolutions,
      reachFraction: span > 0 ? reach / span : 0,
      persistSum: c.votes.reduce((s, v) => s + v.persistenceVoxels, 0),
    };
  });
  const sorted = [...stats].sort(
    (a, b) =>
      b.resolutions.length * b.reachFraction - a.resolutions.length * a.reachFraction ||
      b.persistSum - a.persistSum ||
      byPos(a.c.mean, b.c.mean),
  );
  return sorted.map((s, i) => {
    const branchAt = new Map<number, string>();
    for (const res of s.resolutions) {
      const strongest = s.c.votes
        .filter((v) => v.res === res)
        .sort((a, b) => b.persistenceVoxels - a.persistenceVoxels || a.branchId.localeCompare(b.branchId))[0]!;
      branchAt.set(res, strongest.branchId);
    }
    const best = s.c.votes.reduce((m, v) => (v.persistenceVoxels > m.persistenceVoxels ? v : m));
    return {
      id: `tip-${i + 1}`,
      tip: s.c.mean,
      resolutions: s.resolutions,
      votes: s.resolutions.length,
      reachFraction: s.reachFraction,
      suspect: s.resolutions.length === 1,
      best,
      branchAt,
    };
  });
}

/**
 * Resolve clusters to ONE resolution's branches, geometrically: in rank order,
 * each cluster claims the nearest unclaimed branch tip within `radius`. Works
 * for ANY resolution — including slider positions the sweep never visited —
 * because the match is proximity in world space, not a table lookup.
 * Deterministic; a cluster that claims nothing is simply absent from the map
 * (the caller's `missing`, named to the person).
 */
export function resolveAtResolution(
  clusters: readonly TipCluster[],
  branches: readonly { readonly id: string; readonly tipWorld: Vec3 }[],
  radius: number,
): ReadonlyMap<string, string> {
  const claimed = new Set<string>();
  const out = new Map<string, string>();
  for (const cluster of clusters) {
    let best: string | null = null;
    let bestD = radius;
    for (const b of branches) {
      if (claimed.has(b.id)) continue;
      const d = dist(cluster.tip, b.tipWorld);
      if (d < bestD) {
        bestD = d;
        best = b.id;
      }
    }
    if (best !== null) {
      claimed.add(best);
      out.set(cluster.id, best);
    }
  }
  return out;
}

/**
 * Sweep one hand. Synchronous per resolution but yields to the event loop
 * between stops (a res-80 voxelization is real work; the panel stays alive).
 * A resolution that refuses casts no votes and is REPORTED — that too is data.
 */
export async function consensusScan(
  scene: Object3D,
  candidate: HandCandidate,
  resolutions: readonly number[] = CONSENSUS_SWEEP,
): Promise<TipConsensus> {
  const votes: TipVote[] = [];
  const cleanResolutions: number[] = [];
  const refused: { res: number; reason: string }[] = [];

  for (const res of resolutions) {
    await new Promise((r) => setTimeout(r, 0));
    try {
      const evidence = measureHand(scene, candidate, res);
      cleanResolutions.push(res);
      // Reach anchor: the earliest-parting branch's base — the palm parting
      // line, a stable wrist proxy at every resolution. Reach is a RANKING
      // signal (distal digits outrank wrist artifacts), never a placement input.
      const wristProxy = evidence.branches.reduce(
        (m, b) => (b.baseLevel < m.baseLevel ? b : m),
        evidence.branches[0]!,
      ).baseWorld;
      for (const branch of evidence.branches) {
        votes.push({
          res,
          branchId: branch.id,
          tip: branch.tipWorld,
          persistenceVoxels: branch.persistenceVoxels,
          reach: dist(wristProxy, branch.tipWorld),
        });
      }
    } catch (err) {
      refused.push({ res, reason: err instanceof Error ? err.message : String(err) });
    }
  }

  const span = votes.reduce((m, v) => Math.max(m, v.reach), 0);
  const raw = span > 0 ? clusterVotes(votes, CLUSTER_RADIUS_FRACTION * span) : [];
  return {
    handBone: candidate.handBone,
    clusters: rankClusters(raw, span),
    cleanResolutions,
    refused,
    span,
  };
}
